import { Col, Row } from 'antd';
import classNames from 'classnames';
import { head, startsWith } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Prompt,
  generatePath,
  matchPath,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router';

import routes from '../app/routes';
import Whitespace from '../components/Whitespace';
import { DataStateIndicatorGuard } from '../components/data/dataStateHandlers';
import { DialogsParentContext } from '../components/dialogs/DialogsParentContext';
import { OverrideSettingsContextProvider } from '../components/domainSpecific/settingsElements';
import { FormSanitizationProvider } from '../components/forms/FormSanitizationContext';
import { DynamicFormDependenciesProvider } from '../components/forms/dynamic/dynamicFormDependencies';
import { FormInterceptorContext } from '../components/forms/forms';
import { withPageGuard } from '../components/guards/PageGuard';
import { newShipmentGuard } from '../components/guards/pageGuards';
import { InnerContentScrollbars } from '../components/layout/InnerContentScrollbars';
import { SCROLLBAR_NARROW_TRACK_OFFSET_MARKER } from '../components/layout/Scrollbars.styles';
import { FlexCol, TitleWithExtra } from '../components/layout/layoutElements';
import {
  StepsWithContent,
  isFirstStep,
  isLastStep,
} from '../components/nav/StepsWithContent';
import {
  AccountSelectionDialog,
  NewShipmentSubmitDialog,
  useNewShipmentSubmit,
} from '../dialogs/shipments/newShipmentDialogs';
import NewShipmentFormSchemaProvider from '../forms/shipments/newShipment/NewShipmentFormSchemaProvider';
import { SavedShipmentProvider } from '../forms/shipments/newShipment/SavedShipmentProvider';
import {
  NEW_AIR_RETRIEVAL_STEPS,
  NEW_SHIPMENT_STEPS,
} from '../forms/shipments/newShipment/newShipmentConstants';
import {
  CancelShipmentLink,
  NewShipmentBottomControls,
  NewShipmentFormButtons,
  NewShipmentSubmitButton,
} from '../forms/shipments/newShipment/newShipmentElements';
import { useNewShipmentElementsStyles } from '../forms/shipments/newShipment/newShipmentElements.styles';
import {
  isSavedShipmentModeActive,
  shouldForceTbdServiceType,
} from '../forms/shipments/newShipment/savedShipmentsUtils';
import useNewShipmentFormState, {
  useNewShipmentFormStateSingleForm,
} from '../forms/shipments/newShipment/useNewShipmentFormState';
import useNewShipmentNiceUrl, {
  NewShipmentSubRoute,
} from '../forms/shipments/newShipment/useNewShipmentNiceUrl';
import useNewShipmentStateUpdaters from '../forms/shipments/newShipment/useNewShipmentStateUpdaters';
import useNewShipmentSteps from '../forms/shipments/newShipment/useNewShipmentSteps';
import useSubmitNewShipmentStep from '../forms/shipments/newShipment/useSubmitNewShipmentStep';
import { useShipmentTemplateChangeDetectionInterceptors } from '../forms/shipments/shipmentTemplates/ShipmentTemplateChangedDialog';
import ShipmentTemplatesCard from '../forms/shipments/shipmentTemplates/ShipmentTemplatesCard';
import { useAccounts } from '../hooks/data/auth';
import { cssVariables } from '../styles/cssVariables';
import BaseLoggedPage from '../templates/BaseLoggedPage';
import { useBaseLoggedPageStyles } from '../templates/BaseLoggedPage.styles';
import { pxToNumber } from '../utils/cssUtils';
import ShipmentOrderPreviewCard from '../widgets/shipments/ShipmentOrderPreviewCard';
import { PageExitDialogType } from './nav/PageExitDialog';
import ActiveAccountHeader from '../widgets/ActiveAccountHeader';
import { QuoteChangeWarning } from './QuoteChangeWarning';
import { useQuoteFormValues } from './hooks/useQuoteFormValues';
import { useNewShipmentPageStyles } from './NewShipmentPage.styles';
import { useHasDialogStyles } from '../components/dialogs/hasDialog.styles';
import { useLayoutElementsStyles } from '../components/layout/layoutElements.styles';
import NewSinglePageContent from './NewShipmentSinglePage';

function NewShipmentPageWrapper({ account, title, children }) {
  const { styles: baseLoggedPageStyles } = useBaseLoggedPageStyles();
  const { styles } = useNewShipmentPageStyles(baseLoggedPageStyles);
  const { styles: hasDialogStyles } = useHasDialogStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const computedIsDialogOpen = isDialogOpen || !account;
  const isPartnerAccess = account?.partner;

  return (
    <BaseLoggedPage
      id="NewShipmentPage"
      leftSideContent={<ActiveAccountHeader account={account} />}
      className={classNames(styles.newShipmentPageWrapper, {
        [hasDialogStyles.hasDialog]: computedIsDialogOpen,
      })}
    >
      <DialogsParentContext.Provider
        value={{ isDialogOpen: computedIsDialogOpen, setDialogOpen }}
      >
        <FlexCol className={layoutStyles.flex1}>
          {isPartnerAccess && (
            <div className={styles.partnerAccessTopBar}>
              <FormattedMessage id="book.newShipment.partnerAccess" />
              <Whitespace />|<Whitespace />
              {account.name}
              <Whitespace />
              {account.number}
            </div>
          )}
          {title}
          <InnerContentScrollbars
            className={SCROLLBAR_NARROW_TRACK_OFFSET_MARKER}
          >
            {children}
          </InnerContentScrollbars>
        </FlexCol>
      </DialogsParentContext.Provider>
    </BaseLoggedPage>
  );
}

function NewShipmentExitPrompt({ match, skip }) {
  const { location } = useHistory();
  const isAirRetrieval = !!useRouteMatch(routes.book.newAirRetrieval);

  return (
    <Prompt
      when={
        // Don't open prompt when in account selection - there is nothing to be lost yet
        !skip &&
        `${match.path}/${NewShipmentSubRoute.ACCOUNT_SELECTION}` !==
          location.pathname
      }
      message={({ pathname }) =>
        (!isAirRetrieval && startsWith(pathname, routes.book.newShipment)) || // New shipment subpages are allowed
        (isAirRetrieval && startsWith(pathname, routes.book.newAirRetrieval)) || // New shipment subpages are allowed
        matchPath(pathname, { path: routes.shipmentDetail }) // Redirect to detail in the end is allowed
          ? true
          : PageExitDialogType.CANCEL_SHIPMENT
      }
    />
  );
}

function NewShipmentProviders({
  values,
  account,
  setAccount,
  unitSystem,
  shipmentTemplateChangeDetection,
  children,
  loading = false,
}) {
  const accountsResult = useAccounts();
  const isAirRetrieval = !!useRouteMatch(routes.book.newAirRetrieval);
  const savedShipmentModeActive = isSavedShipmentModeActive(values);
  const forceTbdServiceType = shouldForceTbdServiceType(values);

  return (
    <FormInterceptorContext.Provider
      value={{
        onBlur: shipmentTemplateChangeDetection.onBlur,
        onKeyDown: shipmentTemplateChangeDetection.onKeyDown,
      }}
    >
      <NewShipmentFormSchemaProvider account={account}>
        <DynamicFormDependenciesProvider values={values}>
          <SavedShipmentProvider
            active={savedShipmentModeActive}
            forceTbdServiceType={forceTbdServiceType}
          >
            <OverrideSettingsContextProvider unitSystem={unitSystem}>
              <FormSanitizationProvider>
                <DataStateIndicatorGuard
                  queryResult={{
                    ...accountsResult,
                    loading: accountsResult?.loading || loading,
                    data: accountsResult.customerAccounts,
                  }}
                >
                  {customerAccounts => (
                    <>
                      {children}
                      <AccountSelectionDialog
                        value={account}
                        onChange={setAccount}
                        customerAccounts={customerAccounts}
                        filterFunc={
                          isAirRetrieval
                            ? acc => acc.newAirRetrieval?.enabled
                            : acc => acc.newOrder?.enabled
                        }
                      />
                    </>
                  )}
                </DataStateIndicatorGuard>
              </FormSanitizationProvider>
            </OverrideSettingsContextProvider>
          </SavedShipmentProvider>
        </DynamicFormDependenciesProvider>
      </NewShipmentFormSchemaProvider>
    </FormInterceptorContext.Provider>
  );
}

function NewShipmentPageInner() {
  const isAirRetrieval = !!useRouteMatch(routes.book.newAirRetrieval);
  const match = useRouteMatch();
  const { push, replace } = useHistory();
  const { search } = useLocation();
  const [redirectToQuote, setRedirectToQuote] = useState(false);
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();

  const {
    forms,
    refreshValues,
    account,
    setAccount,
    unitSystem,
    setUnitSystem,
    values,
    extractValues,
  } = useNewShipmentFormState();

  const [activeTemplateCounter, setActiveTemplateCounter] = useState(0);
  const { stepsProps, currentIndex, goToNext, goToPrev, step, resetStep } =
    useNewShipmentSteps({
      forms,
      refreshValues,
      activeTemplateCounter,
      steps: isAirRetrieval ? NEW_AIR_RETRIEVAL_STEPS : NEW_SHIPMENT_STEPS,
      disableUnvisitedSteps: true,
    });

  const { afterApplyTemplate } = useNewShipmentStateUpdaters({
    forms,
    refreshValues,
    setUnitSystem,
  });
  const afterCancelTemplate = useCallback(() => {
    afterApplyTemplate();
    resetStep();
  }, [afterApplyTemplate, resetStep]);
  const onActiveTemplateChange = useCallback(({ fromForm }) => {
    // If form is saved as a template, it's not considered a change of active template
    if (!fromForm) {
      setActiveTemplateCounter(old => old + 1);
    }
  }, []);

  const savedShipmentModeActive = isSavedShipmentModeActive(values);

  const { referenceNo, loading, warningVisible, resetValues } =
    useQuoteFormValues({
      forms,
      account,
      refreshValues,
      setAccount,
      setUnitSystem,
      values,
    });

  const {
    props: shipmentTemplateChangeDetectionProps,
    ...shipmentTemplateChangeDetection
  } = useShipmentTemplateChangeDetectionInterceptors();

  const {
    submit,
    dialogProps: submitDialogProps,
    isConfirming,
  } = useNewShipmentSubmit({ referenceNo });

  const submitStep = useSubmitNewShipmentStep({
    forms,
    stepsProps,
    currentIndex,
    submit,
    extractValues,
    goToNext,
    steps: NEW_SHIPMENT_STEPS,
  });

  useNewShipmentNiceUrl({
    baseUrl: match.url,
    account,
    isConfirming,
    step,
  });

  const disconnectQuote = () => {
    const queryParams = new URLSearchParams(search);
    queryParams.delete('referenceNo');
    replace({
      search: queryParams.toString(),
    });
  };

  const reuseQuote = () => {
    setRedirectToQuote(true);
  };

  useEffect(() => {
    if (redirectToQuote) {
      push(
        generatePath(routes.quoting.editQuote, {
          referenceNo,
        })
      );
    }
  }, [push, referenceNo, redirectToQuote]);

  return (
    <NewShipmentProviders
      account={account}
      setAccount={setAccount}
      unitSystem={unitSystem}
      values={values}
      shipmentTemplateChangeDetection={shipmentTemplateChangeDetection}
      loading={loading}
    >
      <NewShipmentExitPrompt match={match} skip={redirectToQuote} />
      {warningVisible && (
        <QuoteChangeWarning
          onReset={resetValues}
          onQuoteDisconnect={disconnectQuote}
          onQuoteReuse={reuseQuote}
          referenceNo={referenceNo}
        />
      )}
      <NewShipmentPageWrapper
        account={account}
        title={
          <TitleWithExtra
            title={
              <>
                <FormattedMessage
                  id={
                    isAirRetrieval
                      ? 'book.newAirRetrieval.title'
                      : 'book.newShipment.title'
                  }
                />
                <CancelShipmentLink
                  className={classNames(
                    'hide-md-and-smaller',
                    newShipmentElementsStyles.titleCancelLink
                  )}
                />
              </>
            }
            rightColClassName="hide-md-and-smaller"
          >
            <div
              className={classNames(
                'NewShipmentTopControls',
                newShipmentElementsStyles.newShipmentTopControls
              )}
            >
              <NewShipmentFormButtons
                onSubmit={submitStep}
                onBack={goToPrev}
                isFirstStep={isFirstStep(stepsProps)}
                isLastStep={isLastStep(stepsProps)}
              />
            </div>
          </TitleWithExtra>
        }
      >
        <Row
          className="flex-nowrap"
          gutter={pxToNumber(cssVariables.spaceNorm2)}
        >
          <Col
            className={classNames(layoutStyles.flex1, 'spaces-vert-norm1_5')}
          >
            {/* TODO: TEMPLATES FOR AIR RETRIEVAL  */}
            {!isAirRetrieval && (
              <ShipmentTemplatesCard
                forms={forms}
                values={values}
                afterApply={afterApplyTemplate}
                afterCancel={afterCancelTemplate}
                changeDetectionProps={shipmentTemplateChangeDetectionProps}
                onActiveItemChange={onActiveTemplateChange}
              />
            )}
            <StepsWithContent
              {...stepsProps}
              className="spaces-vert-norm1_5"
              segmentedControlClassName="margin-hor-norm"
              clickable
            />
          </Col>
          <Col className="hide-md-and-smaller">
            <ShipmentOrderPreviewCard
              key={activeTemplateCounter}
              account={account}
              values={values}
              stepIndex={currentIndex}
            />
          </Col>
        </Row>
        <NewShipmentBottomControls
          submitStep={submitStep}
          stepsProps={stepsProps}
        />
        <NewShipmentSubmitDialog
          {...submitDialogProps}
          confirmData={submitDialogProps.confirmData}
          savedShipmentModeActive={savedShipmentModeActive}
        />
      </NewShipmentPageWrapper>
    </NewShipmentProviders>
  );
}

function NewShipmentSinglePageInner() {
  const isAirRetrieval = !!useRouteMatch(routes.book.newAirRetrieval);
  const match = useRouteMatch();
  const { push, replace } = useHistory();
  const { search } = useLocation();
  const [redirectToQuote, setRedirectToQuote] = useState(false);
  const { styles: layoutStyles } = useLayoutElementsStyles();
  const { styles: newShipmentElementsStyles } = useNewShipmentElementsStyles();

  const {
    forms,
    refreshValues,
    account,
    setAccount,
    unitSystem,
    setUnitSystem,
    values,
    extractValues,
  } = useNewShipmentFormStateSingleForm();
  const [activeTemplateCounter, setActiveTemplateCounter] = useState(0);

  const { afterApplyTemplate } = useNewShipmentStateUpdaters({
    forms,
    refreshValues,
    setUnitSystem,
  });

  const afterCancelTemplate = useCallback(() => {
    afterApplyTemplate();
  }, [afterApplyTemplate]);

  const onActiveTemplateChange = useCallback(({ fromForm }) => {
    // If form is saved as a template, it's not considered a change of active template
    if (!fromForm) {
      setActiveTemplateCounter(old => old + 1);
    }
  }, []);

  const savedShipmentModeActive = isSavedShipmentModeActive(values);

  const { referenceNo, loading, warningVisible, resetValues } =
    useQuoteFormValues({
      forms,
      account,
      refreshValues,
      setAccount,
      setUnitSystem,
      values,
    });

  const {
    props: shipmentTemplateChangeDetectionProps,
    ...shipmentTemplateChangeDetection
  } = useShipmentTemplateChangeDetectionInterceptors();

  const { submit, dialogProps: submitDialogProps } = useNewShipmentSubmit({
    referenceNo,
  });

  const disconnectQuote = () => {
    const queryParams = new URLSearchParams(search);
    queryParams.delete('referenceNo');
    replace({
      search: queryParams.toString(),
    });
  };

  const reuseQuote = () => {
    setRedirectToQuote(true);
  };

  useEffect(() => {
    if (redirectToQuote) {
      push(
        generatePath(routes.quoting.editQuote, {
          referenceNo,
        })
      );
    }
  }, [push, referenceNo, redirectToQuote]);

  return (
    <NewShipmentProviders
      account={account}
      setAccount={setAccount}
      unitSystem={unitSystem}
      values={values}
      shipmentTemplateChangeDetection={shipmentTemplateChangeDetection}
      loading={loading}
    >
      <NewShipmentExitPrompt match={match} skip={redirectToQuote} />
      {warningVisible && (
        <QuoteChangeWarning
          onReset={resetValues}
          onQuoteDisconnect={disconnectQuote}
          onQuoteReuse={reuseQuote}
          referenceNo={referenceNo}
        />
      )}
      <NewShipmentPageWrapper
        account={account}
        title={
          <TitleWithExtra
            title={
              <>
                <FormattedMessage
                  id={
                    isAirRetrieval
                      ? 'book.newAirRetrieval.title'
                      : 'book.newShipment.title'
                  }
                />
                <CancelShipmentLink className="hide-md-and-smaller" />
              </>
            }
            rightColClassName="hide-md-and-smaller"
          >
            <div
              className={classNames(
                'NewShipmentTopControls',
                newShipmentElementsStyles.newShipmentTopControls
              )}
            >
              <NewShipmentSubmitButton
                form={forms[0]}
                submit={submit}
                extractValues={extractValues}
              />
            </div>
          </TitleWithExtra>
        }
      >
        <Row
          className="flex-nowrap"
          gutter={pxToNumber(cssVariables.spaceNorm2)}
        >
          <Col
            className={classNames(layoutStyles.flex1, 'spaces-vert-norm1_5')}
          >
            {/* TODO: TEMPLATES FOR AIR RETRIEVAL  */}
            {!isAirRetrieval && (
              <ShipmentTemplatesCard
                forms={forms}
                values={values}
                afterApply={afterApplyTemplate}
                afterCancel={afterCancelTemplate}
                changeDetectionProps={shipmentTemplateChangeDetectionProps}
                onActiveItemChange={onActiveTemplateChange}
              />
            )}
            <NewSinglePageContent
              form={head(forms)?.form}
              formRef={head(forms)?.formRef}
              slideActive
              refreshValues={refreshValues}
              activeTemplateCounter={activeTemplateCounter}
              onEditedPackageChange={allValues =>
                requestAnimationFrame(() =>
                  refreshValues({ packages: allValues })
                )
              }
            />
          </Col>
        </Row>
        <NewShipmentSubmitDialog
          {...submitDialogProps}
          confirmData={submitDialogProps.confirmData}
          savedShipmentModeActive={savedShipmentModeActive}
        />
      </NewShipmentPageWrapper>
    </NewShipmentProviders>
  );
}

const NewShipmentPage = withPageGuard(newShipmentGuard)(NewShipmentPageInner);
export default NewShipmentPage;

export const NewSingleShipmentPage = withPageGuard(newShipmentGuard)(
  NewShipmentSinglePageInner
);
